import { DOCUMENT, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
  FULLBLEED_URLS,
  FULLSCREEN_URLS,
  I18nStore,
  isFillHeightRoute,
  isPublicDocsRoute,
  LayoutStore,
  RouterStore,
  ThemeStore,
} from '@indutrust/shared-data';
import {
  APP_BRAND_CONFIG,
  APP_SESSION,
  SIDE_MENU_CATEGORIES_BY_ROLE,
} from '@indutrust/shared-model';
import { LanguageNotificationComponent } from '@indutrust/shared-ui';
import {
  provideTranslocoScopeSharedGlobal,
  resolveSideMenuCategories,
  ScrollLockService,
} from '@indutrust/shared-util';
import { SideMenuContentComponent } from '../side-menu/side-menu-content.component';
import { TopbarComponent } from '../topbar/topbar.component';

@Component({
  selector: 'idt-layout',
  imports: [
    LanguageNotificationComponent,
    NgClass,
    RouterOutlet,
    SideMenuContentComponent,
    TopbarComponent,
  ],
  template: `
    @if (fullScreen()) {
      <!-- Fullscreen layout — no topbar, no sidebar -->
      <router-outlet />
    } @else {
      <div class="layout-wrapper" [ngClass]="containerClass()">
        <!-- Topbar -->
        <idt-topbar
          [publicDocsShell]="publicDocsShell()"
          (logoutRequested)="logout()" />

        <!-- Sidebar -->
        @if (!publicDocsShell()) {
          <div
            class="layout-sidebar border border-surface dark:border-transparent">
            <idt-side-menu-content
              [sideMenuCategories]="sideMenuCategories()" />
          </div>
        }

        <!-- Main content -->
        <div
          class="layout-main-container"
          [class.layout-main-container--fill-height]="fillHeight()"
          [class.layout-main-container--fullbleed]="fullBleed()">
          <div
            class="layout-main"
            [class.layout-main--fill-height]="fillHeight()"
            [class.layout-main--fullbleed]="fullBleed()">
            <router-outlet />
          </div>
          @if (!fullBleed()) {
            <div
              class="flex items-center justify-center gap-2 py-4 border-t border-surface">
              {{ brandConfig.appName }} ©{{ currentYear }}
            </div>
          }
        </div>

        <!-- Overlay mask (click to close mobile sidebar) -->
        @if (!publicDocsShell()) {
          <button
            class="layout-mask"
            tabindex="-1"
            (click)="layoutStore.onMenuToggle()"
            (keydown.escape)="layoutStore.onMenuToggle()"></button>
        }
      </div>

      @if (i18nStore.isLanguageNotificationVisible()) {
        <idt-language-notification
          class="app-notification"
          [language]="i18nStore.language()"
          (closed)="i18nStore.updateIsLanguageNotificationVisible(false)" />
      }
    }
  `,
  styles: `
    .app-notification {
      position: fixed;
      bottom: 1rem;
      inset-inline-end: 1rem;
      z-index: 1100;
    }
  `,
  providers: [provideTranslocoScopeSharedGlobal()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Shared main application layout component.
 *
 * Uses the Sakai-style layout structure with a fixed topbar, sidebar,
 * and main content area. Integrates with ThemeStore for dark mode / presets
 * and LayoutStore for sidebar state. When the mobile or overlay sidebar drawer
 * is open, document scroll is locked on `html` (ADR 007 scroll container) via
 * the `blocked-scroll` body class hook; scroll position is restored on close.
 *
 * Brand-specific configuration is injected via:
 * - {@link APP_BRAND_CONFIG} — app name used in the footer
 * - {@link SIDE_MENU_CATEGORIES_BY_ROLE} — per-role side menu categories
 * - {@link APP_SESSION} — current session; abstracts over the concrete auth store
 */
export class LayoutComponent {
  protected readonly brandConfig = inject(APP_BRAND_CONFIG);
  protected readonly i18nStore = inject(I18nStore);
  protected readonly layoutStore = inject(LayoutStore);
  protected readonly routerStore = inject(RouterStore);
  protected readonly session = inject(APP_SESSION);
  protected readonly themeStore = inject(ThemeStore);

  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly scrollLock = inject(ScrollLockService);
  private readonly sideMenuByRole = inject(SIDE_MENU_CATEGORIES_BY_ROLE);

  readonly fullBleed = computed(() => {
    const url = this.routerStore.url();
    return FULLBLEED_URLS.some((segment) => url.startsWith(segment));
  });
  readonly publicDocsShell = computed(
    () =>
      isPublicDocsRoute(this.routerStore.url()) &&
      !this.session.isAuthenticated()
  );
  readonly containerClass = computed(() => {
    const menuMode = this.themeStore.menuMode();
    const isFullbleed = this.fullBleed();
    const isPublicDocsShell = this.publicDocsShell();
    const staticMenuDesktopInactive =
      this.layoutStore.staticMenuDesktopInactive();
    return {
      'layout-overlay': menuMode === 'overlay',
      'layout-public-docs-shell': isPublicDocsShell,
      'layout-static': menuMode === 'static',
      'layout-static-inactive':
        staticMenuDesktopInactive && menuMode === 'static',
      'layout-overlay-active': this.layoutStore.overlayMenuActive(),
      'layout-mobile-active': this.layoutStore.mobileMenuActive(),
      'layout-fullbleed-sidebar-active':
        isFullbleed && menuMode === 'static' && !staticMenuDesktopInactive,
    };
  });
  readonly fillHeight = computed(() =>
    isFillHeightRoute(this.routerStore.url())
  );
  readonly fullScreen = computed(() => {
    const url = this.routerStore.url();
    return FULLSCREEN_URLS.some((segment) => url.startsWith(segment));
  });
  readonly sidebarScrollLocked = computed(
    () =>
      this.layoutStore.mobileMenuActive() ||
      this.layoutStore.overlayMenuActive()
  );
  readonly sideMenuCategories = computed(() => {
    const userType = this.session.user()?.type;
    let categories = this.sideMenuByRole.supplier;

    if (userType === 'admin') {
      categories = this.sideMenuByRole.admin;
    } else if (userType === 'buyer') {
      categories = this.sideMenuByRole.buyer;
    } else if (userType === 'government') {
      categories = this.sideMenuByRole.government;
    } else if (userType === 'zone') {
      categories = this.sideMenuByRole.zone;
    }

    return resolveSideMenuCategories(categories, {
      companyId: this.session.company()?.id,
    });
  });

  protected readonly currentYear = new Date().getFullYear();

  private sidebarScrollLockY = 0;

  /** Locks `html` scroll while the mobile or overlay sidebar drawer is open. */
  protected readonly blockScrollEffect = effect(() => {
    if (this.sidebarScrollLocked()) {
      this.lockSidebarScroll();
    } else {
      this.releaseSidebarScrollLock();
    }
  });

  protected logout(): void {
    this.session.logout();
  }

  constructor() {
    this.scrollLock.init();
    this.destroyRef.onDestroy(() => this.releaseSidebarScrollLock());
  }

  private lockSidebarScroll(): void {
    const { body } = this.document;
    if (body.classList.contains('blocked-scroll')) {
      return;
    }
    this.sidebarScrollLockY = window.scrollY;
    body.classList.add('blocked-scroll');
  }

  private releaseSidebarScrollLock(): void {
    const { body } = this.document;
    if (!body.classList.contains('blocked-scroll')) {
      return;
    }
    const savedY = this.sidebarScrollLockY;
    body.classList.remove('blocked-scroll');
    window.scrollTo(0, savedY);
  }
}
